import { format } from 'date-fns'
import { useCallback, useState } from 'react'

import { AplDocumentTypes } from '../../../models/aplDocument'
import { uploadAplDocuments } from '../api/uploadAplDocuments'
import { IDocumentFieldChangeCallback } from '../components/documentField'
import { DATE_FORMAT } from '../configs'
import { TDocumentsSet, TDocumentsUploadResults } from '../models'

interface IUseDocumentsFormParameters {
  initialDate: Date
  onSubmitFinish: () => void
}

export const useDocumentsForm = ({ initialDate, onSubmitFinish }: IUseDocumentsFormParameters) => {
  const [isLoading, setIsLoading] = useState(false)
  const [documentsDate, setDocumentsDate] = useState(initialDate)
  const [termsAndConditionsDocuments, setTermsAndConditionsDocuments] = useState<TDocumentsSet>({})
  const [selectiveDistributorCriteriaDocuments, setSelectiveDistributorCriteriaDocuments] =
    useState<TDocumentsSet>({})
  const [tncUploadResults, setTncUploadResults] = useState<TDocumentsUploadResults>({})
  const [sdcUploadResults, setSdcUploadResults] = useState<TDocumentsUploadResults>({})

  const handleDocumentChange = useCallback<IDocumentFieldChangeCallback>(
    ({ documentType, file, language }) => {
      switch (documentType) {
        case 'selective-distributor-criteria':
          setSelectiveDistributorCriteriaDocuments((prevState) => ({
            ...prevState,
            [language]: file,
          }))
          return
        case 'terms-and-conditions':
          setTermsAndConditionsDocuments((prevState) => ({ ...prevState, [language]: file }))
          return
        default:
      }
    },
    []
  )

  const handleSubmit = useCallback(async () => {
    const effectiveDate = format(documentsDate, DATE_FORMAT)

    setTncUploadResults({})
    setSdcUploadResults({})

    setIsLoading(true)
    const tncResponses = await uploadAplDocuments({
      documents: termsAndConditionsDocuments,
      type: AplDocumentTypes.TC,
      effectiveDate,
    })

    const sdcResponses = await uploadAplDocuments({
      documents: selectiveDistributorCriteriaDocuments,
      type: AplDocumentTypes.SDC,
      effectiveDate,
    })

    setSdcUploadResults(
      Object.entries(sdcResponses).reduce((acc, entry) => {
        const [language, response] = entry
        if (response) {
          acc[language] = response
        }

        return acc
      }, {})
    )

    setTncUploadResults(
      Object.entries(tncResponses).reduce((acc, entry) => {
        const [language, response] = entry
        if (response) {
          acc[language] = response
        }

        return acc
      }, {})
    )
    setIsLoading(false)

    onSubmitFinish()
  }, [
    termsAndConditionsDocuments,
    selectiveDistributorCriteriaDocuments,
    documentsDate,
    onSubmitFinish,
  ])

  return {
    documentsDate,
    changeDocumentsDate: setDocumentsDate,
    handleDocumentChange,
    handleSubmit,
    isLoading,

    tncUploadResults,
    sdcUploadResults,
  }
}
